import { useCallback, useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { useTranslation } from 'react-i18next';
import { QuestsRepository } from '../../connectors/repositories/quests';
import { callNotification } from '../notification';
import { parseErrorToUserReadableMessage } from '../format';

// Пока неделя не началась, проверяем раз в минуту
const NO_WEEK_POLL_MS = 60 * 1000;

// silent: не показывать ошибки загрузки (блок на дашборде просто скрывается)
export const useQuests = ({ silent = false } = {}) => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isError, setIsError] = useState(false);

  const load = useCallback(async () => {
    if (!account) {
      setData(null);
      return;
    }
    setIsLoading(true);
    try {
      setData(await QuestsRepository.getCurrent(account));
      setIsError(false);
    } catch (e) {
      setIsError(true);
      if (!silent) {
        callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
      }
    } finally {
      setIsLoading(false);
    }
  }, [account, silent]);

  useEffect(() => {
    load();
  }, [load]);

  // Подтягиваем новую неделю без перезагрузки страницы
  const weekEndsAt = data?.week?.endsAt;
  const hasData = !!data;
  useEffect(() => {
    if (!hasData) return;
    if (!weekEndsAt) {
      const timer = setInterval(load, NO_WEEK_POLL_MS);
      return () => clearInterval(timer);
    }
    const delay = Math.max(0, new Date(weekEndsAt).getTime() - Date.now()) + 5000;
    const timer = setTimeout(load, Math.min(delay, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [hasData, weekEndsAt, load]);

  const checkHold = async () => {
    setPendingAction('hold');
    try {
      const result = await QuestsRepository.checkHold(account);
      callNotification({
        type: result?.passed ? 'success' : 'info',
        message: result?.passed ? t('quests.hold.passed') : t('quests.hold.failed'),
      });
      await load();
    } catch (e) {
      callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
    } finally {
      setPendingAction(null);
    }
  };

  const claim = async (id) => {
    setPendingAction(`claim-${id}`);
    try {
      const result = await QuestsRepository.claim(account, id);
      callNotification({
        type: 'success',
        message: result?.bonus
          ? t('quests.claimedWithBonus', { points: result.claimed, bonus: result.bonus })
          : t('quests.claimed', { points: result?.claimed }),
      });
      await load();
    } catch (e) {
      callNotification({ type: 'error', message: parseErrorToUserReadableMessage(e) });
    } finally {
      setPendingAction(null);
    }
  };

  return { data, isLoading, isError, pendingAction, checkHold, claim, reload: load };
};
